/**
 * AudiencesSection Component
 * Displays hierarchical campaign structure: Ad Sets -> Audiences
 * Shows accurate metrics at the ad set level (where Meta actually tracks them)
 */

import { useState, useEffect } from 'react';
import {
    Users,
    AlertCircle,
    CheckCircle,
    ChevronRight,
    ChevronDown,
    Layers,
    Globe,
    Ban,
    DollarSign,
    Image,
    Play
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { formatCurrency, extractMetricValue, normalizeObjective, getObjectiveConfig } from '../../utils/objectiveHelpers';
import { formatNumber } from '../../utils/helpers';

const API_BASE = 'http://localhost:3001';

const AudiencesSection = ({ campaign }) => {
    const { currentUser } = useAuth();
    const { selectedBrand } = useApp();
    const [adSets, setAdSets] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedAdSets, setExpandedAdSets] = useState(new Set());

    useEffect(() => {
        const fetchHierarchy = async () => {
            const platformId = campaign?.platformCampaignId || campaign?.id;
            if (!platformId || !currentUser || !selectedBrand) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const params = new URLSearchParams({
                    userId: currentUser.uid,
                    brandId: selectedBrand.id,
                    platform: campaign.platform || 'meta'
                });

                const response = await fetch(`${API_BASE}/api/campaigns/${platformId}/hierarchy?${params}`);

                if (response.ok) {
                    const data = await response.json();
                    setAdSets(data.adSets || []);
                    setSummary(data.summary || null);
                } else {
                    const errorData = await response.json().catch(() => ({}));
                    setError(errorData.error || `Failed to fetch data (${response.status})`);
                }
            } catch (err) {
                console.error('Error fetching campaign hierarchy:', err);
                setError('Failed to connect to server.');
            } finally {
                setLoading(false);
            }
        };

        fetchHierarchy();
    }, [campaign?.id, campaign?.platformCampaignId, currentUser, selectedBrand]);

    const toggleAdSet = (adSetId) => {
        setExpandedAdSets(prev => {
            const next = new Set(prev);
            if (next.has(adSetId)) {
                next.delete(adSetId);
            } else {
                next.add(adSetId);
            }
            return next;
        });
    };

    const objective = normalizeObjective(campaign?.objective);
    const config = getObjectiveConfig(objective);

    if (loading) {
        return (
            <div style={{ padding: 'var(--space-4)' }}>
                {[1, 2, 3].map(i => (
                    <div
                        key={i}
                        style={{
                            height: '80px',
                            background: 'var(--color-background)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-2)',
                            animation: 'pulse 2s infinite'
                        }}
                    />
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                padding: 'var(--space-4)',
                textAlign: 'center',
                color: 'var(--color-text-tertiary)'
            }}>
                <AlertCircle size={24} style={{ marginBottom: 'var(--space-2)' }} />
                <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>{error}</p>
            </div>
        );
    }

    if (adSets.length === 0) {
        return (
            <div style={{
                padding: 'var(--space-4)',
                textAlign: 'center',
                color: 'var(--color-text-tertiary)'
            }}>
                <Layers size={24} style={{ marginBottom: 'var(--space-2)', opacity: 0.5 }} />
                <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                    No ad sets found for this campaign
                </p>
            </div>
        );
    }

    // Find best and worst performers
    const adSetsWithSpend = adSets.filter(as => as.metrics?.spend > 0);
    const sortedByPerformance = [...adSetsWithSpend].sort((a, b) => {
        const aValue = extractMetricValue(a.metrics, config.primaryKPI) || 0;
        const bValue = extractMetricValue(b.metrics, config.primaryKPI) || 0;
        return config.lowerIsBetter ? aValue - bValue : bValue - aValue;
    });

    const bestAdSet = sortedByPerformance[0];
    const worstAdSet = sortedByPerformance[sortedByPerformance.length - 1];

    const totalSpend = summary?.totalSpend || 0;

    return (
        <div style={{ paddingTop: 'var(--space-3)' }}>
            {/* Summary Header */}
            {summary && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 'var(--space-3)',
                    marginBottom: 'var(--space-4)',
                    padding: 'var(--space-3)',
                    background: 'var(--color-background)',
                    borderRadius: 'var(--radius-md)'
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>
                            {summary.totalAdSets}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                            Ad Sets
                        </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            fontSize: 'var(--font-size-lg)',
                            fontWeight: 600,
                            color: summary.activeAdSets > 0 ? 'var(--color-success)' : 'var(--color-text-tertiary)'
                        }}>
                            {summary.activeAdSets}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                            Active
                        </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>
                            {formatCurrency(summary.totalSpend)}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                            Total Spend
                        </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            fontSize: 'var(--font-size-lg)',
                            fontWeight: 600,
                            color: summary.overallRoas >= 2 ? 'var(--color-success)' : summary.overallRoas >= 1 ? 'var(--color-warning)' : 'var(--color-error)'
                        }}>
                            {summary.overallRoas.toFixed(2)}x
                        </div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                            Overall ROAS
                        </div>
                    </div>
                </div>
            )}

            {/* Best/Worst Performers */}
            {bestAdSet && worstAdSet && bestAdSet !== worstAdSet && (
                <div style={{
                    display: 'flex',
                    gap: 'var(--space-3)',
                    marginBottom: 'var(--space-4)'
                }}>
                    <div style={{
                        flex: 1,
                        padding: 'var(--space-3)',
                        background: 'var(--color-success-alpha)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--color-success)'
                    }}>
                        <div style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-success)',
                            marginBottom: 'var(--space-1)'
                        }}>
                            Top Performer
                        </div>
                        <div style={{
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: 'var(--color-text-primary)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}>
                            {bestAdSet.name}
                        </div>
                        <div style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-secondary)'
                        }}>
                            {config.formatPrimary(extractMetricValue(bestAdSet.metrics, config.primaryKPI) || 0)}
                        </div>
                    </div>

                    <div style={{
                        flex: 1,
                        padding: 'var(--space-3)',
                        background: 'var(--color-error-alpha)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--color-error)'
                    }}>
                        <div style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-error)',
                            marginBottom: 'var(--space-1)'
                        }}>
                            Needs Attention
                        </div>
                        <div style={{
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: 'var(--color-text-primary)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}>
                            {worstAdSet.name}
                        </div>
                        <div style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-secondary)'
                        }}>
                            {config.formatPrimary(extractMetricValue(worstAdSet.metrics, config.primaryKPI) || 0)}
                        </div>
                    </div>
                </div>
            )}

            {/* Ad Sets List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {adSets.map((adSet) => {
                    const isExpanded = expandedAdSets.has(adSet.id);
                    const spendShare = totalSpend > 0 ? (adSet.metrics?.spend || 0) / totalSpend * 100 : 0;
                    const primaryValue = extractMetricValue(adSet.metrics, config.primaryKPI);
                    const isBest = adSet === bestAdSet;
                    const isWorst = adSet === worstAdSet && adSet !== bestAdSet;
                    const isActive = adSet.status === 'ACTIVE';

                    return (
                        <div
                            key={adSet.id}
                            style={{
                                background: 'var(--color-background)',
                                borderRadius: 'var(--radius-md)',
                                border: isBest
                                    ? '1px solid var(--color-success)'
                                    : isWorst
                                        ? '1px solid var(--color-error)'
                                        : '1px solid transparent',
                                overflow: 'hidden'
                            }}
                        >
                            {/* Ad Set Header */}
                            <div
                                onClick={() => toggleAdSet(adSet.id)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-3)',
                                    padding: 'var(--space-3)',
                                    cursor: 'pointer'
                                }}
                            >
                                {/* Expand Icon */}
                                <div style={{
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'var(--color-text-tertiary)'
                                }}>
                                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                </div>

                                {/* Ad Set Icon */}
                                <div style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: 'var(--radius-md)',
                                    background: isActive ? 'rgba(34, 197, 94, 0.15)' : 'rgba(156, 163, 175, 0.15)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Layers size={18} style={{ color: isActive ? 'rgb(34, 197, 94)' : 'var(--color-text-tertiary)' }} />
                                </div>

                                {/* Ad Set Info */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-2)',
                                        marginBottom: '2px'
                                    }}>
                                        <span style={{
                                            fontSize: 'var(--font-size-sm)',
                                            fontWeight: 500,
                                            color: 'var(--color-text-primary)',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {adSet.name}
                                        </span>
                                        <span style={{
                                            padding: '2px 6px',
                                            borderRadius: 'var(--radius-sm)',
                                            fontSize: 'var(--font-size-xs)',
                                            background: isActive ? 'rgba(34, 197, 94, 0.15)' : 'rgba(156, 163, 175, 0.15)',
                                            color: isActive ? 'rgb(34, 197, 94)' : 'var(--color-text-tertiary)'
                                        }}>
                                            {adSet.status}
                                        </span>
                                        {isBest && <CheckCircle size={12} style={{ color: 'var(--color-success)' }} />}
                                        {isWorst && <AlertCircle size={12} style={{ color: 'var(--color-error)' }} />}
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-2)',
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-tertiary)'
                                    }}>
                                        {adSet.adCount > 0 && (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                                <Image size={10} />
                                                {adSet.adCount} ad{adSet.adCount !== 1 ? 's' : ''}
                                            </span>
                                        )}
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                            <Users size={10} />
                                            {adSet.audienceCount} audience{adSet.audienceCount !== 1 ? 's' : ''}
                                        </span>
                                        {adSet.excludedCount > 0 && (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                                <Ban size={10} />
                                                {adSet.excludedCount} excluded
                                            </span>
                                        )}
                                        {adSet.demographics?.locations?.length > 0 && (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                                <Globe size={10} />
                                                {adSet.demographics.locations.slice(0, 2).join(', ')}
                                                {adSet.demographics.locations.length > 2 && ` +${adSet.demographics.locations.length - 2}`}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Metrics */}
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 600,
                                        color: isBest
                                            ? 'var(--color-success)'
                                            : isWorst
                                                ? 'var(--color-error)'
                                                : 'var(--color-text-primary)'
                                    }}>
                                        {primaryValue !== null ? config.formatPrimary(primaryValue) : '--'}
                                    </div>
                                    <div style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-tertiary)'
                                    }}>
                                        {formatCurrency(adSet.metrics?.spend || 0)} ({spendShare.toFixed(0)}%)
                                    </div>
                                </div>
                            </div>

                            {/* Expanded Content */}
                            {isExpanded && (
                                <div style={{
                                    borderTop: '1px solid var(--color-border)',
                                    background: 'var(--color-surface)'
                                }}>
                                    {/* Metrics Grid */}
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(4, 1fr)',
                                        gap: 'var(--space-2)',
                                        padding: 'var(--space-3)',
                                        background: 'var(--color-background)',
                                        margin: 'var(--space-3)',
                                        borderRadius: 'var(--radius-md)'
                                    }}>
                                        <MetricItem label="Spend" value={formatCurrency(adSet.metrics?.spend || 0)} />
                                        <MetricItem label="Impressions" value={formatNumber(adSet.metrics?.impressions || 0)} />
                                        <MetricItem label="Clicks" value={formatNumber(adSet.metrics?.clicks || 0)} />
                                        <MetricItem label="CTR" value={`${(adSet.metrics?.ctr || 0).toFixed(2)}%`} />
                                        <MetricItem label="Reach" value={formatNumber(adSet.metrics?.reach || 0)} />
                                        <MetricItem label="Conversions" value={formatNumber(adSet.metrics?.conversions || 0)} />
                                        <MetricItem label="Revenue" value={formatCurrency(adSet.metrics?.revenue || 0)} />
                                        <MetricItem
                                            label="ROAS"
                                            value={`${(adSet.metrics?.roas || 0).toFixed(2)}x`}
                                            highlight={adSet.metrics?.roas >= 2}
                                            warning={adSet.metrics?.roas > 0 && adSet.metrics?.roas < 1}
                                        />
                                    </div>

                                    {/* Demographics */}
                                    {(adSet.demographics?.ageRange || adSet.demographics?.genders?.length > 0) && (
                                        <div style={{
                                            padding: '0 var(--space-3) var(--space-3)',
                                            display: 'flex',
                                            gap: 'var(--space-2)',
                                            flexWrap: 'wrap'
                                        }}>
                                            {adSet.demographics.ageRange && (
                                                <span style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    padding: '2px 8px',
                                                    background: 'rgba(59, 130, 246, 0.1)',
                                                    borderRadius: 'var(--radius-sm)',
                                                    color: 'rgb(59, 130, 246)'
                                                }}>
                                                    Age: {adSet.demographics.ageRange}
                                                </span>
                                            )}
                                            {adSet.demographics.genders?.map((gender, i) => (
                                                <span key={i} style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    padding: '2px 8px',
                                                    background: 'rgba(124, 58, 237, 0.1)',
                                                    borderRadius: 'var(--radius-sm)',
                                                    color: 'rgb(124, 58, 237)'
                                                }}>
                                                    {gender}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    {/* Placements */}
                                    {adSet.placements?.length > 0 && (
                                        <div style={{ padding: '0 var(--space-3) var(--space-3)' }}>
                                            <div style={{
                                                fontSize: 'var(--font-size-xs)',
                                                fontWeight: 600,
                                                color: 'var(--color-text-secondary)',
                                                marginBottom: 'var(--space-2)'
                                            }}>
                                                Placements
                                            </div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                {adSet.placements.map((placement, idx) => (
                                                    <span
                                                        key={idx}
                                                        style={{
                                                            fontSize: 'var(--font-size-xs)',
                                                            padding: '3px 8px',
                                                            background: placement.type === 'automatic'
                                                                ? 'rgba(34, 197, 94, 0.1)'
                                                                : getPlatformColor(placement.platform),
                                                            borderRadius: 'var(--radius-sm)',
                                                            color: placement.type === 'automatic'
                                                                ? 'rgb(34, 197, 94)'
                                                                : 'var(--color-text-secondary)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                        }}
                                                    >
                                                        {getPlatformIcon(placement.platform)}
                                                        {placement.name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Audiences List */}
                                    {adSet.audiences?.length > 0 && (
                                        <div style={{ padding: '0 var(--space-3) var(--space-3)' }}>
                                            <div style={{
                                                fontSize: 'var(--font-size-xs)',
                                                fontWeight: 600,
                                                color: 'var(--color-text-secondary)',
                                                marginBottom: 'var(--space-2)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--space-1)'
                                            }}>
                                                <Users size={12} />
                                                Audiences in this Ad Set
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                {adSet.audiences.map((audience, idx) => (
                                                    <div
                                                        key={audience.id || idx}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 'var(--space-2)',
                                                            padding: 'var(--space-2)',
                                                            background: audience.isExcluded
                                                                ? 'rgba(239, 68, 68, 0.05)'
                                                                : 'var(--color-background)',
                                                            borderRadius: 'var(--radius-sm)',
                                                            opacity: audience.isExcluded ? 0.7 : 1
                                                        }}
                                                    >
                                                        <div style={{
                                                            width: '24px',
                                                            height: '24px',
                                                            borderRadius: 'var(--radius-sm)',
                                                            background: getAudienceTypeColor(audience.type),
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '0.8em'
                                                        }}>
                                                            {getAudienceTypeIcon(audience.type)}
                                                        </div>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{
                                                                fontSize: 'var(--font-size-xs)',
                                                                color: audience.isExcluded
                                                                    ? 'var(--color-text-tertiary)'
                                                                    : 'var(--color-text-primary)',
                                                                textDecoration: audience.isExcluded ? 'line-through' : 'none',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {audience.name}
                                                            </div>
                                                        </div>
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 'var(--space-1)'
                                                        }}>
                                                            {audience.isExcluded && (
                                                                <span style={{
                                                                    fontSize: 'var(--font-size-xs)',
                                                                    padding: '1px 4px',
                                                                    background: 'rgba(239, 68, 68, 0.15)',
                                                                    borderRadius: 'var(--radius-sm)',
                                                                    color: 'rgb(239, 68, 68)'
                                                                }}>
                                                                    Excluded
                                                                </span>
                                                            )}
                                                            <span style={{
                                                                fontSize: 'var(--font-size-xs)',
                                                                padding: '1px 4px',
                                                                background: getAudienceTypeColor(audience.type),
                                                                borderRadius: 'var(--radius-sm)',
                                                                color: 'var(--color-text-secondary)'
                                                            }}>
                                                                {audience.type}
                                                            </span>
                                                            {audience.size && (
                                                                <span style={{
                                                                    fontSize: 'var(--font-size-xs)',
                                                                    color: 'var(--color-text-tertiary)'
                                                                }}>
                                                                    {formatNumber(audience.size)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Ads/Creatives */}
                                    {adSet.ads?.length > 0 && (
                                        <div style={{ padding: '0 var(--space-3) var(--space-3)' }}>
                                            <div style={{
                                                fontSize: 'var(--font-size-xs)',
                                                fontWeight: 600,
                                                color: 'var(--color-text-secondary)',
                                                marginBottom: 'var(--space-2)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--space-1)'
                                            }}>
                                                <Image size={12} />
                                                Ads in this Ad Set
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                {adSet.ads.map((ad, idx) => {
                                                    const isActive = ad.status === 'ACTIVE';
                                                    const adRoas = ad.metrics?.spend > 0
                                                        ? ad.metrics.revenue / ad.metrics.spend
                                                        : 0;
                                                    return (
                                                        <div
                                                            key={ad.id || idx}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 'var(--space-3)',
                                                                padding: 'var(--space-2)',
                                                                background: 'var(--color-background)',
                                                                borderRadius: 'var(--radius-sm)'
                                                            }}
                                                        >
                                                            {/* Thumbnail */}
                                                            <div style={{
                                                                width: '48px',
                                                                height: '48px',
                                                                borderRadius: 'var(--radius-sm)',
                                                                background: 'var(--color-surface-hover)',
                                                                overflow: 'hidden',
                                                                flexShrink: 0,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                position: 'relative'
                                                            }}>
                                                                {ad.creative?.thumbnailUrl ? (
                                                                    <img
                                                                        src={ad.creative.thumbnailUrl}
                                                                        alt={ad.name}
                                                                        style={{
                                                                            width: '100%',
                                                                            height: '100%',
                                                                            objectFit: 'cover'
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <Image size={20} style={{ color: 'var(--color-text-tertiary)' }} />
                                                                )}
                                                                {ad.creative?.isVideo && (
                                                                    <div style={{
                                                                        position: 'absolute',
                                                                        bottom: '2px',
                                                                        right: '2px',
                                                                        background: 'rgba(0,0,0,0.7)',
                                                                        borderRadius: '2px',
                                                                        padding: '1px 3px'
                                                                    }}>
                                                                        <Play size={8} style={{ color: 'white' }} />
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Ad Info */}
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 'var(--space-2)',
                                                                    marginBottom: '2px'
                                                                }}>
                                                                    <span style={{
                                                                        fontSize: 'var(--font-size-xs)',
                                                                        fontWeight: 500,
                                                                        color: 'var(--color-text-primary)',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        {ad.name}
                                                                    </span>
                                                                    <span style={{
                                                                        padding: '1px 4px',
                                                                        borderRadius: 'var(--radius-sm)',
                                                                        fontSize: '10px',
                                                                        background: isActive ? 'rgba(34, 197, 94, 0.15)' : 'rgba(156, 163, 175, 0.15)',
                                                                        color: isActive ? 'rgb(34, 197, 94)' : 'var(--color-text-tertiary)'
                                                                    }}>
                                                                        {ad.status}
                                                                    </span>
                                                                </div>
                                                                <div style={{
                                                                    fontSize: 'var(--font-size-xs)',
                                                                    color: 'var(--color-text-tertiary)',
                                                                    display: 'flex',
                                                                    gap: 'var(--space-2)'
                                                                }}>
                                                                    <span>{formatNumber(ad.metrics?.impressions || 0)} impr</span>
                                                                    <span>{formatNumber(ad.metrics?.clicks || 0)} clicks</span>
                                                                    <span>{(ad.metrics?.ctr || 0).toFixed(2)}% CTR</span>
                                                                </div>
                                                            </div>

                                                            {/* Ad Metrics */}
                                                            <div style={{ textAlign: 'right' }}>
                                                                <div style={{
                                                                    fontSize: 'var(--font-size-xs)',
                                                                    fontWeight: 600,
                                                                    color: adRoas >= 2 ? 'var(--color-success)' : adRoas > 0 && adRoas < 1 ? 'var(--color-error)' : 'var(--color-text-primary)'
                                                                }}>
                                                                    {adRoas > 0 ? `${adRoas.toFixed(2)}x` : '--'}
                                                                </div>
                                                                <div style={{
                                                                    fontSize: 'var(--font-size-xs)',
                                                                    color: 'var(--color-text-tertiary)'
                                                                }}>
                                                                    {formatCurrency(ad.metrics?.spend || 0)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Budget Info */}
                                    {(adSet.budget?.daily || adSet.budget?.lifetime) && (
                                        <div style={{
                                            padding: 'var(--space-2) var(--space-3)',
                                            borderTop: '1px solid var(--color-border)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-3)',
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-text-tertiary)'
                                        }}>
                                            <DollarSign size={12} />
                                            {adSet.budget.daily && (
                                                <span>Daily: {formatCurrency(adSet.budget.daily)}</span>
                                            )}
                                            {adSet.budget.lifetime && (
                                                <span>Lifetime: {formatCurrency(adSet.budget.lifetime)}</span>
                                            )}
                                            {adSet.optimizationGoal && (
                                                <span style={{ marginLeft: 'auto' }}>
                                                    Optimizing for: {adSet.optimizationGoal.replace(/_/g, ' ')}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Metric Item Component
const MetricItem = ({ label, value, highlight, warning }) => (
    <div style={{ textAlign: 'center' }}>
        <div style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            color: highlight ? 'var(--color-success)' : warning ? 'var(--color-error)' : 'var(--color-text-primary)'
        }}>
            {value}
        </div>
        <div style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-tertiary)'
        }}>
            {label}
        </div>
    </div>
);

// Helper functions
const getAudienceTypeIcon = (type) => {
    const icons = {
        'Lookalike': '👥',
        'Custom': '🎯',
        'Interest': '💡',
        'Demographic': '👤',
        'Retargeting': '🔄',
        'Saved': '💾',
        'Behavior': '🎯',
        'Engagement': '💬'
    };
    return icons[type] || '👥';
};

const getAudienceTypeColor = (type) => {
    const colors = {
        'Lookalike': 'rgba(124, 58, 237, 0.15)',
        'Custom': 'rgba(34, 197, 94, 0.15)',
        'Interest': 'rgba(249, 115, 22, 0.15)',
        'Demographic': 'rgba(59, 130, 246, 0.15)',
        'Retargeting': 'rgba(236, 72, 153, 0.15)',
        'Saved': 'rgba(156, 163, 175, 0.15)',
        'Behavior': 'rgba(139, 92, 246, 0.15)',
        'Engagement': 'rgba(20, 184, 166, 0.15)'
    };
    return colors[type] || 'var(--color-surface-hover)';
};

const getPlatformIcon = (platform) => {
    const icons = {
        'facebook': '📘',
        'instagram': '📷',
        'messenger': '💬',
        'audience_network': '🌐',
        'all': '✨'
    };
    return icons[platform] || '📱';
};

const getPlatformColor = (platform) => {
    const colors = {
        'facebook': 'rgba(24, 119, 242, 0.1)',
        'instagram': 'rgba(225, 48, 108, 0.1)',
        'messenger': 'rgba(0, 132, 255, 0.1)',
        'audience_network': 'rgba(156, 163, 175, 0.1)',
        'all': 'rgba(34, 197, 94, 0.1)'
    };
    return colors[platform] || 'rgba(156, 163, 175, 0.1)';
};

export default AudiencesSection;
